import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, X } from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { TextArea } from "@/components/TextArea";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
  createSectorSchema,
  type CreateSectorFormData,
} from "@/schemas/sectorSchemas";
import { useCreateSector } from "@/hooks/useSectors";
import { useGetUsers } from "@/hooks/useUsers";
import { useDebounce } from "@/hooks/useDebounce";
import { applyPhoneMask, removePhoneMask } from "@/lib/utils";
import { useState } from "react";

export const CreateSector = () => {
  const navigate = useNavigate();
  const createSector = useCreateSector();
  const [userSearch, setUserSearch] = useState("");
  const debouncedSearch = useDebounce(userSearch, 500);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  // Buscar usuários com roles específicas e search com debounce
  const { data: usersData } = useGetUsers({
    search: debouncedSearch,
    role: "ADMIN,AGENT,TRIAGE", // Filtrar apenas esses roles
  });

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useForm<CreateSectorFormData>({
    resolver: zodResolver(createSectorSchema),
    defaultValues: {
      status: "ACTIVE",
      hasRegionals: false,
    },
  });

  const onSubmit = async (data: CreateSectorFormData) => {
    await createSector.mutateAsync(
      {
        name: data.name,
        acronym: data.acronym,
        description: data.description,
        email: data.email,
        phone: removePhoneMask(data.phone),
        workingHoursStart: data.workingHoursStart,
        workingHoursEnd: data.workingHoursEnd,
        responsibleId: data.responsibleId,
        status: data.status,
        hasRegionals: data.hasRegionals,
      },
      {
        onSuccess: () => {
          navigate("/sectors");
          toast.success("Setor criado com sucesso!");
        },
      },
    );
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <HeaderPage
        title="Criar Setor"
        description="Informe os dados que o setor terá"
      />

      <div className="flex-1 overflow-y-auto mt-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-white">
            <CardContentUI className="p-4">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <h3 className="text-base font-semibold mb-3">
                    Informações Básicas
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      title="Nome do Setor"
                      isRequired
                      placeholder="Ex: Secretaria de Obras"
                      error={errors.name?.message}
                      {...register("name")}
                    />

                    <Input
                      title="Sigla"
                      isRequired
                      placeholder="Ex: SEMOB"
                      error={errors.acronym?.message}
                      {...register("acronym")}
                    />
                  </div>

                  <div className="mt-4">
                    <TextArea
                      title="Descrição"
                      isRequired
                      placeholder="Descreva as responsabilidades do setor"
                      {...register("description")}
                    />
                    {errors.description && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.description.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Informações de Contato */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Informações de Contato
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      title="Email"
                      isRequired
                      type="email"
                      placeholder="setor@municipio.gov.br"
                      error={errors.email?.message}
                      {...register("email")}
                    />

                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <Input
                          title="Telefone"
                          isRequired
                          placeholder="(11) 91234-5678"
                          error={errors.phone?.message}
                          value={field.value}
                          onChange={(e) => {
                            const maskedValue = applyPhoneMask(e.target.value);
                            field.onChange(maskedValue);
                          }}
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Horário de Funcionamento
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      title="Horário de Início"
                      isRequired
                      type="time"
                      error={errors.workingHoursStart?.message}
                      {...register("workingHoursStart")}
                    />

                    <Input
                      title="Horário de Término"
                      isRequired
                      type="time"
                      error={errors.workingHoursEnd?.message}
                      {...register("workingHoursEnd")}
                    />
                  </div>
                </div>

                {/* Configurações */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Configurações</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Responsável do Setor */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Responsável <span className="text-red-500">*</span>
                      </label>
                      <Controller
                        name="responsibleId"
                        control={control}
                        render={({ field }) => (
                          <div className="relative">
                            <div className="relative">
                              <Input
                                placeholder="Buscar responsável..."
                                value={selectedUser ? selectedUser.profile?.fullName : userSearch}
                                onChange={(e) => {
                                  setUserSearch(e.target.value);
                                  setSelectedUser(null);
                                  field.onChange("");
                                  setShowDropdown(true);
                                }}
                                onFocus={() => setShowDropdown(true)}
                                error={errors.responsibleId?.message}
                              />
                              {selectedUser && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedUser(null);
                                    setUserSearch("");
                                    field.onChange("");
                                  }}
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                            
                            {showDropdown && !selectedUser && usersData?.data && usersData.data.length > 0 && (
                              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[280px] overflow-y-auto">
                                {usersData.data.map((user: any) => (
                                  <div
                                    key={user.id}
                                    onClick={() => {
                                      setSelectedUser(user);
                                      field.onChange(user.id);
                                      setShowDropdown(false);
                                      setUserSearch("");
                                    }}
                                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                                  >
                                    <div className="flex flex-col">
                                      <span className="font-medium text-sm">
                                        {user.profile?.fullName}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {user.email} •{" "}
                                        {user.role === "ADMIN"
                                          ? "Administrador"
                                          : user.role === "AGENT"
                                            ? "Agente"
                                            : "Triagem"}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {showDropdown && !selectedUser && debouncedSearch && usersData?.data?.length === 0 && (
                              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                                <div className="px-4 py-6 text-center text-sm text-gray-500">
                                  Nenhum usuário encontrado
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      />
                    </div>

                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <SelectTrigger title="Status" isRequired>
                            <SelectValue placeholder="Selecione o status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ACTIVE">Ativo</SelectItem>
                            <SelectItem value="INACTIVE">Inativo</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.status && (
                      <p className="text-sm text-red-500 col-span-2">
                        {errors.status.message}
                      </p>
                    )}

                    <Controller
                      name="hasRegionals"
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={(value) =>
                            field.onChange(value === "true")
                          }
                          defaultValue={field.value ? "true" : "false"}
                        >
                          <SelectTrigger title="Possui Regionais">
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="false">Não</SelectItem>
                            <SelectItem value="true">Sim</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/sectors")}
                    disabled={createSector.isPending}
                    className="cursor-pointer"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    disabled={createSector.isPending}
                    isLoading={createSector.isPending}
                    className="text-white cursor-pointer"
                  >
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Criar Setor
                    </>
                  </Button>
                </div>
              </form>
            </CardContentUI>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
